/* eslint-disable @typescript-eslint/no-explicit-any */
import { COMPOSITE_TYPE } from './constants';
import {
  AQLCompositeQuery,
  AQLDurationQuery,
  AQLOriginalURLQuery,
  AQLTitleQuery,
  AQLPublishedDateQuery,
  AQLCreatedAtQuery,
  AQLUpdatedAtQuery,
  AQLGroupsQuery,
  AQLTranscriptQuery,
  AQLIdQuery,
  AQLNameQuery,
  AQLTypeQuery,
  AQLBrandSafetyGarmFilterQuery,
  AQLBrandSafetyGarmQuery,
  AQLBrandSafetyIabFilterQuery,
  AQLBrandSafetyIabQuery,
  QUERY_CLASS_MAPPER,
  AQLContextualIABQuery,
  AQLNamedEntitiesFilterQuery,
  AQLNamedEntitiesQuery,
  AQLLinkedNamedEntitiesQuery,
  AQLLinkedNamedEntitiesFilterQuery,
  AQLAudioCountQuery,
  AQLExternalIdFilterQuery,
  AQLExternalIdsQuery,
  AQLPodcastMetadataFilterQuery,
  AQLPodcastMetadataQuery,
  AQLLanguageQuery,
} from './queries';
import {
  AQLPodcastMetadataFilterType,
  AQLQuery,
  AQLQueryCondition,
  AQLQueryType,
  AQLRangeCondition,
  CompositeType,
} from './types';
import {
  CategoryType,
  GarmRiskLevelType,
  IabBrandSafetyCategoryType,
  IabBrandSafetyRiskLevelType,
} from '@insights_module/types/BrandSafety';
import { IABFilterType } from '@insights_module/types/ContextualAndEntity';

export class AQLBaseQueryBuilder {
  /* 
  The "query" array is used as the main store for the queries. If there are 
  no composite queries being built currently, any new queries get directly added
  to this array.
  */
  protected query: AQLQuery[] = [];

  /*
  The "stack" array holds the information about any ongoing composite queries.
  Each element of the array is an object that contains the type of composite query ('all', 'any', or 'none')
  and the list of queries associated with that composite query. When a new composite query is started,
  a new object is pushed to the stack. When a composite query ends, its object gets removed from the stack.
  */
  protected stack: { queries: AQLQuery[]; type: CompositeType }[] = [];

  constructor(stack?: { queries: AQLQuery[]; type: CompositeType }[]) {
    this.stack = stack ?? [];
  }

  protected push(query: AQLQuery) {
    if (this.stack.length > 0) {
      this.stack[this.stack.length - 1].queries.push(query);
    } else {
      this.query.push(query);
    }
  }

  getQueries(): AQLQuery[] {
    // if there are any composite queries still being built, properly close them
    while (this.stack.length > 0) {
      const stackTop = this.stack[this.stack.length - 1];
      const compositeQuery = new AQLCompositeQuery(stackTop.type, stackTop.queries);
      this.stack.pop();
      this.push(compositeQuery as AQLQuery);
    }
    return this.query;
  }
  // method that should use as a top level method to get the query, in difference to getQueries which is used recursively and must return array of queries
  build(): AQLQuery {
    return this.getQueries()[0];
  }
}

class AQLInsideQueryBuilder extends AQLBaseQueryBuilder {
  addQuery = createAddQuery<AQLInsideQueryBuilder>();
  addTitleQuery = createAddTitleQuery<AQLInsideQueryBuilder>();
  addDurationQuery = createAddDurationQuery<AQLInsideQueryBuilder>();
  addOriginal_urlQuery = createAddOriginalURLQuery<AQLInsideQueryBuilder>();
  addPublished_dateQuery = createAddPublishedDateQuery<AQLInsideQueryBuilder>();
  addCreated_atQuery = createAddCreatedAtQuery<AQLInsideQueryBuilder>();
  addUpdated_atQuery = createAddUpdatedAtQuery<AQLInsideQueryBuilder>();
  addGroupQuery = createAddGroupQuery<AQLInsideQueryBuilder>();
  addLanguageQuery = createAddLanguageQuery<AQLInsideQueryBuilder>();

  addTranscriptQuery = createAddTranscriptQuery<AQLInsideQueryBuilder>();
  addGarmQuery = createAddGarmQuery<AQLInsideQueryBuilder>();
  addIABBrandSafetyQuery = createAddIabBrandSafetyQuery<AQLInsideQueryBuilder>();
  addIABContextualQuery = createAddIabContextualQuery<AQLInsideQueryBuilder>();
  addNamedEntitiesQuery = createAddNamedEntitiesQuery<AQLInsideQueryBuilder>();
  addLinkedNamedEntitiesQuery = createAddLinkedNamedEntitiesQuery<AQLInsideQueryBuilder>();

  addIDQuery = createAddIDQuery<AQLInsideQueryBuilder>();
  addNameQuery = createAddNameQuery<AQLInsideQueryBuilder>();
  addTypeQuery = createAddTypeQuery<AQLInsideQueryBuilder>();
  addAudioCountQuery = createAddAudioCountQuery<AQLInsideQueryBuilder>();
  addExternalIdQuery = createAddExternalIdsQuery<AQLInsideQueryBuilder>();
  addPodcastMetadataQuery = createAddPodcastMetadataQuery<AQLInsideQueryBuilder>();

  all = createAllQuery<AQLInsideQueryBuilder>();
  any = createAnyQuery<AQLInsideQueryBuilder>();
  none = createNoneQuery<AQLInsideQueryBuilder>();
}

/*
  The AQLQueryBuilder is the one of two entry points for the user. It only exposes the composite methods (all/any/none)
  and after each call, it returns an AQLInsideQueryBuilder instance, which provides methods for adding specific types of queries.
*/
export class AQLQueryBuilder extends AQLBaseQueryBuilder {
  all(callback: (q: AQLInsideQueryBuilder) => void): AQLQueryBuilder {
    const builder = new AQLInsideQueryBuilder();
    callback(builder);
    const compositeQuery = new AQLCompositeQuery(COMPOSITE_TYPE.ALL, builder.getQueries());
    this.push(compositeQuery as AQLQuery);
    return this;
  }
  any(callback: (q: AQLInsideQueryBuilder) => void): AQLQueryBuilder {
    const builder = new AQLInsideQueryBuilder();
    callback(builder);
    const compositeQuery = new AQLCompositeQuery(COMPOSITE_TYPE.ANY, builder.getQueries());
    this.push(compositeQuery as AQLQuery);
    return this;
  }
  none(callback: (q: AQLInsideQueryBuilder) => void): AQLQueryBuilder {
    const builder = new AQLInsideQueryBuilder();
    callback(builder);
    const compositeQuery = new AQLCompositeQuery(COMPOSITE_TYPE.NONE, builder.getQueries());
    this.push(compositeQuery as AQLQuery);
    return this;
  }
  composite(type: CompositeType, callback: (q: AQLInsideQueryBuilder) => void): AQLQueryBuilder {
    const builder = new AQLInsideQueryBuilder();
    callback(builder);
    const compositeQuery = new AQLCompositeQuery(type, builder.getQueries());
    this.push(compositeQuery as AQLQuery);
    return this;
  }
}

/*
  The AQLFlatQueryBuilder serves as an second entry point for the user. It provides all the query-building methods 
  including specific query type additions (like 'addTitleQuery', 'addDurationQuery' and so on) and composite query type additions (like 'all', 'any', 'none').
  Differing from the AQLQueryBuilder, this class does not instantiate a new AQLInsideQueryBuilder on each composite method call. Instead, 
  it continuously returns the same AQLFlatQueryBuilder instance. This design allows the user to chain multiple method calls seamlessly on the same line.
 */
export class AQLFlatQueryBuilder extends AQLBaseQueryBuilder {
  /*
  The "stack" array holds the information about any ongoing composite queries.
  With 'start' and 'end' methods, the user can start and end composite queries, like using brackets.
  */
  start(type: CompositeType): AQLFlatQueryBuilder {
    this.stack.push({ queries: [], type: type });
    return this;
  }

  end(type: CompositeType): AQLFlatQueryBuilder {
    if (this.stack.length > 0 && this.stack[this.stack.length - 1].type === type) {
      const { queries } = this.stack.pop() as { queries: AQLQuery[]; type: CompositeType };
      const query = new AQLCompositeQuery(type, queries);
      this.push(query as AQLQuery);
    } else {
      console.error(
        `Mismatch in composite type. You are trying to end a "${type}" composite while in a 
        "${this.stack.length > 0 ? this.stack[this.stack.length - 1].type : 'NONE'}" composite.`
      );
    }
    return this;
  }

  addQuery = createAddQuery<AQLFlatQueryBuilder>();
  addTitleQuery = createAddTitleQuery<AQLFlatQueryBuilder>();
  addDurationQuery = createAddDurationQuery<AQLFlatQueryBuilder>();
  addOriginalAirDateQuery = createAddOriginalURLQuery<AQLFlatQueryBuilder>();
  addPublishedDateQuery = createAddPublishedDateQuery<AQLFlatQueryBuilder>();
  addCreatedAtQuery = createAddCreatedAtQuery<AQLFlatQueryBuilder>();
  addUpdatedAtQuery = createAddUpdatedAtQuery<AQLFlatQueryBuilder>();
  addGroupQuery = createAddGroupQuery<AQLFlatQueryBuilder>();
  addLanguageQuery = createAddLanguageQuery<AQLFlatQueryBuilder>();
  addTranscriptQuery = createAddTranscriptQuery<AQLFlatQueryBuilder>();
  addIDQuery = createAddIDQuery<AQLFlatQueryBuilder>();
  addNameQuery = createAddNameQuery<AQLFlatQueryBuilder>();
  addTypeQuery = createAddTypeQuery<AQLFlatQueryBuilder>();
  addGarmQuery = createAddGarmQuery<AQLFlatQueryBuilder>();
  addIabBrandSafetyQuery = createAddIabBrandSafetyQuery<AQLFlatQueryBuilder>();
  addIABContextualQuery = createAddIabContextualQuery<AQLFlatQueryBuilder>();
  addNamedEntitiesQuery = createAddNamedEntitiesQuery<AQLFlatQueryBuilder>();
  addLinkedNamedEntitiesQuery = createAddLinkedNamedEntitiesQuery<AQLFlatQueryBuilder>();
  addAudioCountQuery = createAddAudioCountQuery<AQLBaseQueryBuilder>();
  addExternalIdQuery = createAddExternalIdsQuery<AQLFlatQueryBuilder>();
  addPodcastMetadataQuery = createAddPodcastMetadataQuery<AQLFlatQueryBuilder>();
}

class GroupFilterQueryBuilder extends AQLBaseQueryBuilder {
  addIDQuery = createAddIDQuery<AQLBaseQueryBuilder>();
}

class GarmFilterQueryBuilder extends AQLBaseQueryBuilder {
  addCategoryQuery = createAddGarmFilterQuery<GarmFilterQueryBuilder>();
}

class IabBrandSafetyFilterQueryBuilder extends AQLBaseQueryBuilder {
  addIabBrandSafetyCategoryQuery =
    createAddIabBrandSafetyFilterQuery<IabBrandSafetyFilterQueryBuilder>();
}

class NamedEntitiesQueryBuilder extends AQLBaseQueryBuilder {
  addNamedEntityQuery = createAddNamedEntitiesFilterQuery<NamedEntitiesQueryBuilder>();
}

class LinkedNamedEntitiesQueryBuilder extends AQLBaseQueryBuilder {
  addLinkedNamedEntitiesQuery =
    createAddLinkedNamedEntitiesFilterQuery<LinkedNamedEntitiesQueryBuilder>();
}

class ExternalIdsQueryBuilder extends AQLBaseQueryBuilder {
  addExternalIdQuery = createExternalIdFilterQuery<ExternalIdsQueryBuilder>();
}

class PodcastMetadataQueryBuilder extends AQLBaseQueryBuilder {
  addPodcastMetadataFilterQuery =
    createAddPodcastMetadataFilterQuery<PodcastMetadataQueryBuilder>();
}

function createAddQuery<T extends AQLBaseQueryBuilder>() {
  /**
   * Creates and adds a new query of the given type to the query builder. Use this with caution, as it is not type-safe.
   *
   * @this {T} This context is inferred from the Query Builder instance calling this method.
   * @param {AQLQueryType} queryType - The type of the query to create and add.
   * @param {...any[]} args - The arguments to pass to the constructor of the query.
   * @returns {T} The Query Builder instance, to enable method chaining.
   *
   * @throws Will throw an error if the provided query type does not correspond to a known query class.
   */
  return function addQuery(this: T, queryType: AQLQueryType, ...args: any[]): T {
    const QueryClass = QUERY_CLASS_MAPPER[queryType];
    if (QueryClass) {
      try {
        const queryInstance = new (QueryClass as any)(...args);
        this.push(queryInstance as AQLQuery);
      } catch (e) {
        console.error(`Error creating query instance for query class: ${QueryClass}`);
      }
    } else {
      console.error(`Unknown query class for type: ${queryType}`);
    }
    return this;
  };
}

function createAllQuery<T extends AQLInsideQueryBuilder>() {
  /**
   * Starts a new 'all' composite query and applies the given callback to it.
   *
   * @this {T} The Query Builder instance calling this method.
   * @param {Function} callback - The function that exposes the Query Builder instance for adding queries to the 'all' composite query.
   * @returns {T} The Query Builder instance, to enable method chaining.
   */
  return function all(this: T, callback: (q: T) => void): T {
    const compositeQueries: AQLQuery[] = [];
    this.stack.push({ queries: compositeQueries, type: COMPOSITE_TYPE.ALL });
    callback(this);
    const compositeQuery = new AQLCompositeQuery(COMPOSITE_TYPE.ALL, compositeQueries);
    this.stack.pop();
    this.push(compositeQuery as AQLQuery);
    return this;
  };
}

function createAnyQuery<T extends AQLBaseQueryBuilder>() {
  /**
   * Starts a new 'any' composite query and applies the given callback to it.
   *
   * @this {T} The Query Builder instance calling this method.
   * @param {Function} callback - The function that exposes the Query Builder instance for adding queries to the 'any' composite query.
   * @returns {T} The Query Builder instance, to enable method chaining.
   */
  return function any(this: T, callback: (q: T) => void): T {
    const compositeQueries: AQLQuery[] = [];
    this.stack.push({ queries: compositeQueries, type: COMPOSITE_TYPE.ANY });
    callback(this);
    const compositeQuery = new AQLCompositeQuery(COMPOSITE_TYPE.ANY, compositeQueries);
    this.stack.pop();
    this.push(compositeQuery as AQLQuery);
    return this;
  };
}

function createNoneQuery<T extends AQLBaseQueryBuilder>() {
  /**
   * Starts a new 'none' composite query and applies the given callback to it.
   *
   * @this {T} The Query Builder instance calling this method.
   * @param {Function} callback - The function that exposes the Query Builder instance for adding queries to the 'none' composite query.
   * @returns {T} The Query Builder instance, to enable method chaining.
   */
  return function none(this: T, callback: (q: T) => void): T {
    const compositeQueries: AQLQuery[] = [];
    this.stack.push({ queries: compositeQueries, type: COMPOSITE_TYPE.NONE });
    callback(this);
    const compositeQuery = new AQLCompositeQuery(COMPOSITE_TYPE.NONE, compositeQueries);
    this.stack.pop();
    this.push(compositeQuery as AQLQuery);
    return this;
  };
}

function createAddTitleQuery<T extends AQLBaseQueryBuilder>() {
  /**
   * Adds a new title query to the Query Builder with the given condition and title.
   *
   * @this {T} The Query Builder instance calling this method.
   * @param {string} condition - The condition of the title query. Either `includes` or `excludes`.
   * @param {string} title - The title value.
   * @returns {T} The Query Builder instance, to enable method chaining.
   */
  return function addTitleQuery(
    this: T,
    condition: Extract<keyof AQLQueryCondition, 'includes' | 'excludes'>,
    title: string
  ): T {
    this.push(new AQLTitleQuery(condition, title) as AQLQuery);
    return this;
  };
}

function createAddDurationQuery<T extends AQLBaseQueryBuilder>() {
  /**
   * Adds a new duration query to the Query Builder with the given condition and duration.
   *
   * @this {T} The Query Builder instance calling this method.
   * @param {string} condition - The condition of the duration query `eq` or `range`.
   * @param {number | AQLRangeCondition<number>} duration - The duration or range of duration to query.
   * @returns {T} The Query Builder instance, to enable method chaining.
   */
  return function addDurationQuery(
    this: T,
    condition: Extract<keyof AQLQueryCondition, 'eq' | 'range'>,
    duration: number | AQLRangeCondition<number>
  ): T {
    if (condition === 'eq' && typeof duration === 'number') {
      this.push(new AQLDurationQuery(condition, duration) as AQLQuery);
    } else if (condition === 'range' && typeof duration === 'object') {
      this.push(new AQLDurationQuery(condition, duration) as AQLQuery);
    } else {
      console.error('Invalid condition or duration for AQLDurationQuery');
    }
    return this;
  };
}

function createAddOriginalURLQuery<T extends AQLBaseQueryBuilder>() {
  /**
   * Adds a new original URL query to the Query Builder with the given condition and original URL.
   *
   * @param {string} condition - The condition of the original URL query `eq` or `includes`.
   * @param {string} originalUrl - The original URL to query.
   * @returns {T} The Query Builder instance, to enable method chaining.
   */
  return function addOriginalAirDateQuery(
    this: T,
    condition: Extract<keyof AQLQueryCondition, 'eq'>,
    originalUrl: string
  ): T {
    this.push(new AQLOriginalURLQuery(condition, originalUrl) as AQLQuery);
    return this;
  };
}

function createAddPublishedDateQuery<T extends AQLBaseQueryBuilder>() {
  /**
   * Adds a new published date query to the Query Builder with the given condition and published date.
   *
   * @param {string} condition - The condition of the published date query `eq` or `range`.
   * @param {string | AQLRangeCondition<string>} publishedDate - The published date or range of published dates to query.
   * @returns {T} The Query Builder instance, to enable method chaining.
   */
  return function addPublishedDateQuery(
    this: T,
    condition: Extract<keyof AQLQueryCondition, 'eq' | 'range'>,
    publishedDate: string | AQLRangeCondition<string>
  ): T {
    if (condition === 'eq' && typeof publishedDate === 'string') {
      this.push(new AQLPublishedDateQuery(condition, publishedDate) as AQLQuery);
    } else if (condition === 'range' && typeof publishedDate === 'object') {
      this.push(new AQLPublishedDateQuery(condition, publishedDate) as AQLQuery);
    } else {
      console.error('Invalid condition or publishedDate for AQLPublishedDateQuery');
    }
    return this;
  };
}

function createAddCreatedAtQuery<T extends AQLBaseQueryBuilder>() {
  /**
   * Adds a new created at query to the Query Builder with the given condition and created at.
   *
   * @param {string} condition - The condition of the created at query `eq` or `range`.
   * @param {string | AQLRangeCondition<string>} createdAt - The created at or range of created ats to query.
   * @returns {T} The Query Builder instance, to enable method chaining.
   *  */
  return function addCreatedAtQuery(
    this: T,
    condition: Extract<keyof AQLQueryCondition, 'eq' | 'range'>,
    createdAt: string | AQLRangeCondition<string>
  ): T {
    if (condition === 'eq' && typeof createdAt === 'string') {
      this.push(new AQLCreatedAtQuery(condition, createdAt) as AQLQuery);
    } else if (condition === 'range' && typeof createdAt === 'object') {
      this.push(new AQLCreatedAtQuery(condition, createdAt) as AQLQuery);
    } else {
      console.error('Invalid condition or createdAt for AQLCreatedAtQuery');
    }
    return this;
  };
}

function createAddUpdatedAtQuery<T extends AQLBaseQueryBuilder>() {
  /**
   * Adds a new updated at query to the Query Builder with the given condition and updated at.
   *
   * @param {string} condition - The condition of the updated at query `eq` or `range`.
   * @param {string | AQLRangeCondition<string>} updatedAt - The updated at or range of updated ats to query.
   * @returns {T} The Query Builder instance, to enable method chaining.
   * */
  return function addUpdatedAtQuery(
    this: T,
    condition: Extract<keyof AQLQueryCondition, 'eq' | 'range'>,
    updatedAt: string | AQLRangeCondition<string>
  ): T {
    if (condition === 'eq' && typeof updatedAt === 'string') {
      this.push(new AQLUpdatedAtQuery(condition, updatedAt) as AQLQuery);
    } else if (condition === 'range' && typeof updatedAt === 'object') {
      this.push(new AQLUpdatedAtQuery(condition, updatedAt) as AQLQuery);
    } else {
      console.error('Invalid condition or updatedAt for AQLUpdatedAtQuery');
    }
    return this;
  };
}

function createAddGroupQuery<T extends AQLBaseQueryBuilder>() {
  /**
   * Adds a new group query to the Query Builder with the given condition and group filter.
   *
   * @param {string} condition - The condition of the group query `includes` or `excludes`.
   * @param {function} callback - The callback function to build the group filter, for now only group id as filter is supported.
   * @returns {T} The Query Builder instance, to enable method chaining.
   * */
  return function addGroupQuery(
    this: T,
    condition: Extract<keyof AQLQueryCondition, 'includes' | 'excludes'>,
    callback: (q: GroupFilterQueryBuilder) => void
  ): T {
    const filterBuilder = new GroupFilterQueryBuilder();
    callback(filterBuilder);
    const groupQuery = new AQLGroupsQuery(condition, filterBuilder.build()); // Assuming only one filter query can be added
    this.push(groupQuery as AQLQuery);
    return this;
  };
}

function createAddLanguageQuery<T extends AQLBaseQueryBuilder>() {
  /**
   * Adds a new language query to the Query Builder with the given condition and language.
   * @param {string} condition - The condition of the language query `eq` or `excludes`.
   * @param {string} language - The language to query.
   * @returns {T} The Query Builder instance, to enable method chaining.
   */
  return function addLanguageQuery(
    this: T,
    condition: Extract<keyof AQLQueryCondition, 'eq' | 'excludes' | 'any_of'>,
    language: string | string[]
  ): T {
    if ((condition === 'eq' || condition === 'excludes') && typeof language === 'string') {
      this.push(new AQLLanguageQuery(condition, language) as AQLQuery);
    } else if (condition === 'any_of' && Array.isArray(language)) {
      this.push(new AQLLanguageQuery(condition, language) as AQLQuery);
    } else {
      console.error('Invalid condition for AQLLanguageQuery');
    }
    return this;
  };
}

function createAddTranscriptQuery<T extends AQLBaseQueryBuilder>() {
  /**
   * Adds a new transcript query to the Query Builder with the given condition and transcript.
   *
   * @param {string} condition - The condition of the transcript query `includes` or `excludes`.
   * @param {string} transcript - The transcript to query.
   * @returns {T} The Query Builder instance, to enable method chaining.
   * */
  return function addTranscriptQuery(
    this: T,
    condition: Extract<keyof AQLQueryCondition, 'includes' | 'excludes'>,
    transcript: string
  ): T {
    this.push(new AQLTranscriptQuery(condition, transcript) as AQLQuery);
    return this;
  };
}

function createAddIDQuery<T extends AQLBaseQueryBuilder>() {
  /**
   * Adds a new id query to the Query Builder with the given condition and show id.
   *
   * @param {string} condition - The condition of the id query `eq` or `any_of`.
   * @param {string | string[]} id - The id or array of ids to query.
   * @returns {T} The Query Builder instance, to enable method chaining.
   * */
  return function addIDQuery(
    this: T,
    condition: Extract<keyof AQLQueryCondition, 'eq' | 'any_of'>,
    id: string | string[]
  ): T {
    if (condition === 'eq' && typeof id === 'string') {
      this.push(new AQLIdQuery(condition, id) as AQLQuery);
    } else if (condition === 'any_of' && Array.isArray(id)) {
      this.push(new AQLIdQuery(condition, id) as AQLQuery);
    } else {
      console.error('Invalid condition for AQLIdQuery');
    }
    return this;
  };
}

function createAddNameQuery<T extends AQLBaseQueryBuilder>() {
  /**
   * Adds a new name query to the Query Builder with the given condition and name of group.
   *
   * @param {string} condition - The condition of the name query `includes` or `excludes`.
   * @param {string} name - The name to query.
   * @returns {T} The Query Builder instance, to enable method chaining.
   * */
  return function addNameQuery(
    this: T,
    condition: Extract<keyof AQLQueryCondition, 'includes' | 'excludes'>,
    name: string
  ): T {
    this.push(new AQLNameQuery(condition, name) as AQLQuery);
    return this;
  };
}

function createAddTypeQuery<T extends AQLBaseQueryBuilder>() {
  /**
   * Adds a new type query to the Query Builder with the given condition and type of group, e.g. `show`.
   *
   * @param {string} condition - The condition of the type query `eq` or `any_of`.
   * @param {string | string[]} type - The type or array of types to query.
   * @returns {T} The Query Builder instance, to enable method chaining.
   * */
  return function addTypeQuery(
    this: T,
    condition: Extract<keyof AQLQueryCondition, 'eq' | 'any_of'>,
    type: string | string[]
  ): T {
    if (condition === 'eq' && typeof type === 'string') {
      this.push(new AQLTypeQuery(condition, type) as AQLQuery);
    } else if (condition === 'any_of' && Array.isArray(type)) {
      this.push(new AQLTypeQuery(condition, type) as AQLQuery);
    } else {
      console.error('Invalid condition for AQLTypeQuery');
    }
    return this;
  };
}

function createAddGarmFilterQuery<T extends AQLBaseQueryBuilder>() {
  /**
   * Adds a new garm filter query to the Garm Query Builder with the given category, condition and garm filter.
   *
   * @param {CategoryType} category - The category of the garm filter, e.g. `arms_ammunition`.
   * @param {string} condition - The condition of the garm filter query `eq`, `range` or `any_of`.
   * @param {string | AQLRangeCondition | string[]} garmFilter - The garm filter or array of garm filters to query.
   * @returns {T} The Query Builder instance, to enable method chaining.
   * */
  return function addGarmFilterQuery(
    this: T,
    category: CategoryType,
    condition: Extract<keyof AQLQueryCondition, 'eq' | 'range' | 'any_of'>,
    garmFilter: GarmRiskLevelType | AQLRangeCondition<GarmRiskLevelType> | GarmRiskLevelType[]
  ): T {
    if (condition === 'eq' && typeof garmFilter === 'string') {
      this.push(new AQLBrandSafetyGarmFilterQuery(category, condition, garmFilter) as AQLQuery);
    } else if (condition === 'range' && typeof garmFilter === 'object') {
      this.push(
        new AQLBrandSafetyGarmFilterQuery(
          category,
          condition,
          garmFilter as AQLRangeCondition<GarmRiskLevelType>
        ) as AQLQuery
      );
    } else if (condition === 'any_of' && Array.isArray(garmFilter)) {
      this.push(new AQLBrandSafetyGarmFilterQuery(category, condition, garmFilter) as AQLQuery);
    } else {
      console.error('Invalid condition for AQLBrandSafetyGarmFilterQuery');
    }
    return this;
  };
}

function createAddGarmQuery<T extends AQLBaseQueryBuilder>() {
  /**
   * Adds a new garm query to the Query Builder with the given callback.
   *
   * @param {Function} callback - The callback function to add garm filter queries to the garm query.
   * @returns {T} The Query Builder instance, to enable method chaining.
   * */
  return function addGarmQuery(this: T, callback: (q: GarmFilterQueryBuilder) => void): T {
    const filterBuilder = new GarmFilterQueryBuilder();
    callback(filterBuilder);
    const garmQuery = new AQLBrandSafetyGarmQuery(filterBuilder.getQueries());
    this.push(garmQuery as AQLQuery);
    return this;
  };
}

function createAddIabBrandSafetyFilterQuery<T extends AQLBaseQueryBuilder>() {
  /**
   * Adds a new iab brand safety filter query to the Query Builder with the given category, condition and iab brand safety filter.
   *
   * @param {IABFilterType} iabCategoryName - The iab brand safety category to query. (e.g. 'military_conflict')
   * @param {string} condition - The condition of the iab brand safety filter query `eq`, `range` or `any_of`.
   * @param {string | AQLRangeCondition | string[]} iabFilter - The iab brand safety filter or array of iab brand safety filters to query.
   * @returns {T} The Query Builder instance, to enable method chaining.
   * */
  return function addIabBrandSafetyFilterQuery(
    this: T,
    iabCategoryName: IabBrandSafetyCategoryType,
    condition: Extract<keyof AQLQueryCondition, 'eq' | 'range' | 'any_of'>,
    iabFilter:
      | IabBrandSafetyRiskLevelType
      | AQLRangeCondition<IabBrandSafetyRiskLevelType>
      | IabBrandSafetyRiskLevelType[]
  ): T {
    if (condition === 'eq' && typeof iabFilter === 'string') {
      this.push(
        new AQLBrandSafetyIabFilterQuery(iabCategoryName, condition, iabFilter) as AQLQuery
      );
    } else if (condition === 'range' && typeof iabFilter === 'object') {
      this.push(
        new AQLBrandSafetyIabFilterQuery(
          iabCategoryName,
          condition,
          iabFilter as AQLRangeCondition<IabBrandSafetyRiskLevelType>
        ) as AQLQuery
      );
    } else if (condition === 'any_of' && Array.isArray(iabFilter)) {
      this.push(
        new AQLBrandSafetyIabFilterQuery(iabCategoryName, condition, iabFilter) as AQLQuery
      );
    } else {
      console.error('Invalid condition for AQLBrandSafetyIabFilterQuery');
    }
    return this;
  };
}

function createAddIabBrandSafetyQuery<T extends AQLBaseQueryBuilder>() {
  /**
   * Adds a new iab brand safety query to the Query Builder with the given callback.
   *
   * @param {Function} callback - The callback function to add iab brand safety filter queries to the iab brand safety query.
   * @returns {T} The Query Builder instance, to enable method chaining.
   * */
  return function addIabBrandSafetyQuery(
    this: T,
    callback: (q: IabBrandSafetyFilterQueryBuilder) => void
  ): T {
    const filterBuilder = new IabBrandSafetyFilterQueryBuilder();
    callback(filterBuilder);
    const iabBrandSafetyQuery = new AQLBrandSafetyIabQuery(filterBuilder.getQueries());
    this.push(iabBrandSafetyQuery as AQLQuery);
    return this;
  };
}

function createAddIabContextualQuery<T extends AQLBaseQueryBuilder>() {
  /**
   * Adds a new iab contextual query to the Query Builder with the given condition and iab contextual.
   * @param {string} condition - The condition of the iab contextual query `includes` or `excludes`.
   * @param {IABFilterType} iabCategoryName - The iab contextual to query. (e.g. 'Books and Literature')
   *
   */

  return function addIabContextualQuery(
    this: T,
    condition: Extract<keyof AQLQueryCondition, 'includes' | 'excludes'>,
    iabCategoryName: IABFilterType
  ): T {
    this.push(new AQLContextualIABQuery(condition, iabCategoryName) as AQLQuery);
    return this;
  };
}

function createAddNamedEntitiesFilterQuery<T extends AQLBaseQueryBuilder>() {
  /**
   * Adds a new named entity filter query to the Query Builder with the given condition and value.
   * @param {string} condition - The condition of the named entity filter query `eq`, `any_of` or `wildcard`.
   * @param {string | string[]} value - The value or array of values to query.
   * @returns {T} The Query Builder instance, to enable method chaining.
   * */

  return function addNamedEntitiesFilterQuery(
    this: T,
    condition: Extract<keyof AQLQueryCondition, 'eq' | 'any_of' | 'wildcard'>,
    value: string | string[]
  ): T {
    if ((condition === 'eq' || condition === 'wildcard') && typeof value === 'string') {
      this.push(new AQLNamedEntitiesFilterQuery('value', condition, value) as AQLQuery);
    } else if (condition === 'any_of' && Array.isArray(value)) {
      this.push(new AQLNamedEntitiesFilterQuery('value', condition, value) as AQLQuery);
    } else {
      console.error('Invalid condition for AQLNamedEntitiesFilterQuery');
    }
    return this;
  };
}

function createAddNamedEntitiesQuery<T extends AQLBaseQueryBuilder>() {
  /**
   * Adds a new named entity query to the Query Builder with the given condition and callback.
   * @param {string} condition - The condition of the named entity query `includes` or `excludes`.
   * @param {Function} callback - The callback function to add named entity filter queries to the named entity query.
   * @returns {T} The Query Builder instance, to enable method chaining.
   * */

  return function addNamedEntitiesQuery(
    this: T,
    condition: Extract<keyof AQLQueryCondition, 'includes' | 'excludes'>,
    callback: (q: NamedEntitiesQueryBuilder) => void
  ): T {
    const filterBuilder = new NamedEntitiesQueryBuilder();
    callback(filterBuilder);
    const namedEntitiesQuery = new AQLNamedEntitiesQuery(condition, filterBuilder.getQueries());
    this.push(namedEntitiesQuery as AQLQuery);
    return this;
  };
}

function createAddLinkedNamedEntitiesFilterQuery<T extends AQLBaseQueryBuilder>() {
  /**
   * Adds a new linkedNamedEntities filter query to the Query Builder with the given condition and value.
   * @param {string} condition - The condition of the linkedNamedEntities filter query `eq` or `wildcard`.
   * @param {string} id - The LinkedNameEntity id to query.
   * @returns {T} The Query Builder instance, to enable method chaining.
   * */

  return function addLinkedNamedEntitiesFilterQuery(
    this: T,
    condition: Extract<keyof AQLQueryCondition, 'eq' | 'any_of' | 'wildcard'>,
    id: string | string[]
  ): T {
    if ((condition === 'eq' || condition === 'wildcard') && typeof id === 'string') {
      this.push(new AQLLinkedNamedEntitiesFilterQuery('id', condition, id) as AQLQuery);
    } else if (condition === 'any_of' && Array.isArray(id)) {
      this.push(new AQLLinkedNamedEntitiesFilterQuery('id', condition, id) as AQLQuery);
    } else {
      console.error('Invalid condition for AQLLinkedNamedEntitiesFilterQuery');
    }
    return this;
  };
}

function createAddLinkedNamedEntitiesQuery<T extends AQLBaseQueryBuilder>() {
  /**
   * Adds a new linkedNamedEntities query to the Query Builder with the given condition and callback.
   * @param {string} condition - The condition of the linkedNamedEntities query `includes` or `excludes`.
   * @param {Function} callback - The callback function to add linkedNamedEntities filter queries to the linkedNamedEntities query.
   * @returns {T} The Query Builder instance, to enable method chaining.
   * */

  return function addLinkedNamedEntitiesQuery(
    this: T,
    condition: Extract<keyof AQLQueryCondition, 'includes' | 'excludes'>,
    callback: (q: LinkedNamedEntitiesQueryBuilder) => void
  ): T {
    const filterBuilder = new LinkedNamedEntitiesQueryBuilder();
    callback(filterBuilder);
    const linkedNamedEntitiesQuery = new AQLLinkedNamedEntitiesQuery(
      condition,
      filterBuilder.getQueries()
    );
    this.push(linkedNamedEntitiesQuery as AQLQuery);
    return this;
  };
}

function createAddAudioCountQuery<T extends AQLBaseQueryBuilder>() {
  /**
   * Adds a new audio count query to the Query Builder with the given condition and audio count.
   *
   * @param {string} condition - The condition of the audio count query `eq` or `range`.
   * @param {number | AQLRangeCondition<number>} audioCount - The audio count or range of audio counts to query.
   * @returns {T} The Query Builder instance, to enable method chaining.
   * */

  return function addAudioCountQuery(
    this: T,
    condition: Extract<keyof AQLQueryCondition, 'eq' | 'range'>,
    audioCount: number | AQLRangeCondition<number>
  ): T {
    if (condition === 'eq' && typeof audioCount === 'number') {
      this.push(new AQLAudioCountQuery(condition, audioCount) as AQLQuery);
    } else if (condition === 'range' && typeof audioCount === 'object') {
      this.push(new AQLAudioCountQuery(condition, audioCount) as AQLQuery);
    } else {
      console.error('Invalid condition or audioCount for AQLDurationQuery');
    }
    return this;
  };
}

function createExternalIdFilterQuery<T extends AQLBaseQueryBuilder>() {
  /**
   * Adds a new external id filter query to the Query Builder with the given type and value.
   * @param {string} type - The value of the external id filter query `name` or `value`.
   * @param {string | number} value - The value or array of values to query.
   * @returns {T} The Query Builder instance, to enable method chaining.
   * */

  return function addExternalIdFilterQuery(
    this: T,
    type: Extract<keyof AQLQueryCondition, 'name' | 'value'>,
    value: string | number
  ): T {
    if (
      (type === 'name' || type === 'value') &&
      (typeof value === 'string' || typeof value === 'number')
    ) {
      this.push(new AQLExternalIdFilterQuery(type, value) as AQLQuery);
    } else {
      console.error('Invalid type or value for AQLExternalIdFilterQuery');
    }
    return this;
  };
}

function createAddExternalIdsQuery<T extends AQLBaseQueryBuilder>() {
  /**
   * Adds a new external id query to the Query Builder with the given condition and callback.
   * @param {string} condition - The condition of the external id query `includes` or `excludes`.
   * @param {Function} callback - The callback function to add external id filter queries to the external id query.
   * @returns {T} The Query Builder instance, to enable method chaining.
   * */

  return function addExternalIdsQuery(
    this: T,
    condition: Extract<keyof AQLQueryCondition, 'includes' | 'excludes'>,
    callback: (q: ExternalIdsQueryBuilder) => void
  ): T {
    const filterBuilder = new ExternalIdsQueryBuilder();
    callback(filterBuilder);
    const externalIdsQuery = new AQLExternalIdsQuery(condition, filterBuilder.getQueries());
    this.push(externalIdsQuery as AQLQuery);
    return this;
  };
}

function createAddPodcastMetadataFilterQuery<T extends AQLBaseQueryBuilder>() {
  /**
   * Adds a new podcast metadata filter query to the Query Builder with the given condition and value.
   * @param {string} metadataFilterType - The type of the metadata filter query `author`, `apple_star_rating`, `apple_rating_count`, `apple_categories` or `podcast_type`.
   * @param {string} condition - The condition of the metadata filter query `eq`, `range`, `includes` or `excludes`.
   * @param {string | AQLRangeCondition<number>} value - The value to query.
   * @returns {T} The Query Builder instance, to enable method chaining.
   * */

  return function addPodcastMetadataFilterQuery(
    this: T,
    metadataFilterType: AQLPodcastMetadataFilterType,
    condition: Extract<keyof AQLQueryCondition, 'eq' | 'range' | 'includes' | 'excludes'>,
    value: string | AQLRangeCondition<number>
  ): T {
    if (
      (condition === 'eq' || condition === 'includes' || condition === 'excludes') &&
      typeof value === 'string'
    ) {
      this.push(
        new AQLPodcastMetadataFilterQuery(metadataFilterType, condition, value) as AQLQuery
      );
    } else if (condition === 'range' && typeof value === 'object') {
      this.push(
        new AQLPodcastMetadataFilterQuery(
          metadataFilterType,
          condition,
          value as AQLRangeCondition<number>
        ) as AQLQuery
      );
    } else {
      console.error('Invalid condition or value for AQLPodcastMetadataFilterQuery');
    }
    return this;
  };
}

function createAddPodcastMetadataQuery<T extends AQLBaseQueryBuilder>() {
  /**
   * Adds a new podcast metadata query to the Query Builder with the given condition and callback.
   * @param {callback} callback - The callback function to add podcast metadata filter queries to the podcast metadata query.
   * @returns {T} The Query Builder instance, to enable method chaining.
   * */

  return function addPodcastMetadataQuery(
    this: T,
    callback: (q: PodcastMetadataQueryBuilder) => void
  ): T {
    const filterBuilder = new PodcastMetadataQueryBuilder();
    callback(filterBuilder);
    const podcastMetadataQuery = new AQLPodcastMetadataQuery(filterBuilder.getQueries());
    this.push(podcastMetadataQuery as AQLQuery);
    return this;
  };
}
